/**
 * Collection card pager
 *
 * Shopify can only paginate a collection by products (250 at most per page), but the grid renders cards: a family of
 * sibling colors collapses into one card, and image/video blocks take one or several cells of a row. This element
 * therefore takes over pagination on the client side:
 *
 *   1. the section renders the current Shopify page (up to 250 products);
 *   2. the element fetches the other Shopify pages of the same collection in the background and merges their cards
 *      into the grid, so the whole collection is known;
 *   3. it splits the tiles into pages by counting grid cells (the first page has its own size), shows one page at a
 *      time and renders a numbered pagination that mirrors the theme's own.
 *
 * The current page is kept in the `p` query parameter. Without JavaScript, the full Shopify page is shown and the
 * theme's regular pagination is rendered inside a <noscript> tag.
 *
 * Expected attributes: section-id, shopify-page, shopify-pages, first-page-size, page-size, and the translated labels
 * count-one, count-other (e.g. "1 product" / "2 products"), label-go-to-page (with __PAGE__ placeholder).
 * Expected children: a <product-list>, a <nav data-pager-nav> with <template data-pager-prev> and
 * <template data-pager-next>.
 */

const ITEM_SELECTOR = 'product-list > product-card, product-list > .product-list__promo';
const PAGE_PARAM = 'p';

/**
 * Number of grid cells an item occupies. Promo blocks carry their column and row span as CSS custom properties,
 * highlighted cards span 2x2, everything else is a single cell.
 */
export function cellWeight(item) {
  if (item.classList.contains('product-list__promo')) {
    const columns = parseInt(item.style.getPropertyValue('--product-list-promo-column-span'), 10) || 1;
    const rows = parseInt(item.style.getPropertyValue('--product-list-promo-row-span'), 10) || 1;
    return columns * rows;
  }

  return item.classList.contains('product-card--highlight') ? 4 : 1;
}

/**
 * Splits items into pages. Each page takes items until at least its number of cells is filled: the first page uses
 * `firstPageSize`, the following ones `pageSize`. An item straddling the boundary belongs to the page it started on,
 * so a page never ends with an unexplained gap. Returns [start, end) index pairs.
 */
export function computePageRanges(weights, firstPageSize, pageSize) {
  const ranges = [];
  let start = 0;

  while (start < weights.length) {
    const target = Math.max(1, ranges.length === 0 ? firstPageSize : pageSize);
    let cells = 0;
    let end = start;

    while (end < weights.length && cells < target) {
      cells += weights[end];
      end += 1;
    }

    ranges.push([start, end]);
    start = end;
  }

  return ranges;
}

/**
 * Page numbers to display for a pagination with `total` pages: every page when there are few, otherwise the first,
 * the last and the neighbours of the current one, with null marking a gap.
 */
export function paginationParts(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages = new Set([1, total, current - 1, current, current + 1].filter((page) => page >= 1 && page <= total));
  const sorted = [...pages].sort((a, b) => a - b);
  const parts = [];

  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) {
      parts.push(null);
    }

    parts.push(page);
  });

  return parts;
}

/** Replaces the number in a translated count ("2 Produkte") with the actual count. */
export function formatCount(templates, count) {
  const template = count === 1 ? templates.one : templates.other;
  return (template || '').replace(/\d[\d.,]*/, String(count));
}

function defineCollectionCardPager() {
  class CollectionCardPager extends HTMLElement {
    currentPage = 1;
    ranges = [];
    abortController = null;
    loaded = false;

    connectedCallback() {
      this.productList = this.querySelector('product-list');
      this.nav = this.querySelector('[data-pager-nav]');
      this.nav?.addEventListener('click', this.onNavClick);

      const requestedPage = parseInt(new URL(window.location.href).searchParams.get(PAGE_PARAM), 10);
      this.currentPage = Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1;

      this.update({ scroll: false });
      this.loadOtherShopifyPages();
    }

    disconnectedCallback() {
      this.nav?.removeEventListener('click', this.onNavClick);
      this.abortController?.abort();
    }

    get firstPageSize() {
      return parseInt(this.getAttribute('first-page-size'), 10) || parseInt(this.getAttribute('page-size'), 10) || 16;
    }

    get pageSize() {
      return parseInt(this.getAttribute('page-size'), 10) || this.firstPageSize;
    }

    get items() {
      return Array.from(this.querySelectorAll(ITEM_SELECTOR));
    }

    /**
     * The section only rendered one Shopify page of products. Fetch the others (same URL, other `page` values, section
     * rendering API) and merge their cards into the grid in collection order.
     */
    async loadOtherShopifyPages() {
      const shopifyPages = parseInt(this.getAttribute('shopify-pages'), 10) || 1;
      const currentShopifyPage = parseInt(this.getAttribute('shopify-page'), 10) || 1;
      const sectionId = this.getAttribute('section-id');

      if (shopifyPages <= 1 || !sectionId || !this.productList) {
        this.loaded = true;
        this.renderNav();
        return;
      }

      this.nav?.setAttribute('aria-busy', 'true');
      this.abortController = new AbortController();

      const currentItems = this.items;
      const pageNumbers = Array.from({ length: shopifyPages }, (_, index) => index + 1);

      const results = await Promise.all(pageNumbers.map(async (page) => {
        if (page === currentShopifyPage) {
          return currentItems;
        }

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', sectionId);
          url.searchParams.delete(PAGE_PARAM);
          url.hash = '';

          const response = await fetch(url.toString(), { signal: this.abortController.signal });
          const html = await response.text();
          const fragment = new DOMParser().parseFromString(html, 'text/html');

          return Array.from(fragment.querySelectorAll(ITEM_SELECTOR)).map((item) => {
            item.removeAttribute('reveal-on-scroll');
            return document.importNode(item, true);
          });
        } catch (error) {
          return error?.name === 'AbortError' ? null : [];
        }
      }));

      if (!this.isConnected || results.includes(null)) {
        return;
      }

      this.productList.append(...results.flat());
      this.nav?.removeAttribute('aria-busy');
      this.loaded = true;
      this.update({ scroll: false });
      this.updateCount();
    }

    onNavClick = (event) => {
      const button = event.target.closest('[data-page]');

      if (!button) {
        return;
      }

      event.preventDefault();
      this.goToPage(parseInt(button.dataset.page, 10));
    };

    goToPage(page) {
      if (page === this.currentPage) {
        return;
      }

      this.currentPage = page;
      this.update({ scroll: true });
    }

    update({ scroll }) {
      const items = this.items;
      this.ranges = computePageRanges(items.map(cellWeight), this.firstPageSize, this.pageSize);
      this.currentPage = Math.min(Math.max(1, this.currentPage), Math.max(1, this.ranges.length));

      const [start, end] = this.ranges[this.currentPage - 1] || [0, items.length];

      items.forEach((item, index) => {
        const visible = index >= start && index < end;

        if (!visible) {
          item.hidden = true;
          return;
        }

        if (item.hidden) {
          item.hidden = false;

          // The theme fades cards in when the list scrolls into view. A card revealed later must not stay stuck at
          // opacity 0, so it opts out of that animation.
          item.removeAttribute('reveal-on-scroll');
          item.style.removeProperty('opacity');
          item.style.removeProperty('transform');
        }
      });

      this.renderNav();
      this.syncUrl();

      if (scroll) {
        (this.closest('.collection') || this).scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    renderNav() {
      if (!this.nav) {
        return;
      }

      const total = this.ranges.length;

      // While other Shopify pages are still loading the page count is not final: keep the navigation out of sight
      // rather than showing a number that is about to change.
      this.nav.hidden = total <= 1 || !this.loaded;

      if (this.nav.hidden) {
        return;
      }

      const goToPageLabel = this.getAttribute('label-go-to-page') || 'Go to page __PAGE__';
      const nodes = [];

      const arrow = (templateSelector, page) => {
        const template = this.nav.querySelector(templateSelector);
        const button = template?.content.firstElementChild?.cloneNode(true);

        if (!button) {
          return null;
        }

        button.dataset.page = String(page);
        button.setAttribute('aria-label', goToPageLabel.replace('__PAGE__', String(page)));
        return button;
      };

      if (this.currentPage > 1) {
        nodes.push(arrow('[data-pager-prev]', this.currentPage - 1));
      }

      paginationParts(this.currentPage, total).forEach((part) => {
        if (part === null) {
          const gap = document.createElement('span');
          gap.className = 'pagination__link subheading';
          gap.textContent = '…';
          nodes.push(gap);
          return;
        }

        if (part === this.currentPage) {
          const current = document.createElement('span');
          current.className = 'pagination__link subheading';
          current.setAttribute('aria-current', 'page');
          current.textContent = String(part);
          nodes.push(current);
          return;
        }

        const link = document.createElement('button');
        link.type = 'button';
        link.className = 'pagination__link subheading';
        link.dataset.page = String(part);
        link.setAttribute('aria-label', goToPageLabel.replace('__PAGE__', String(part)));
        link.textContent = String(part);
        nodes.push(link);
      });

      if (this.currentPage < total) {
        nodes.push(arrow('[data-pager-next]', this.currentPage + 1));
      }

      this.nav.querySelectorAll(':scope > :not(template)').forEach((node) => node.remove());
      this.nav.append(...nodes.filter(Boolean));
    }

    /** The section counted the cards of one Shopify page only; once every page is merged, show the real total. */
    updateCount() {
      const target = this.closest('.collection')?.querySelector('[data-products-count]');

      if (!target) {
        return;
      }

      const cards = this.querySelectorAll('product-list > product-card').length;
      target.textContent = formatCount({ one: this.getAttribute('count-one'), other: this.getAttribute('count-other') }, cards);
    }

    syncUrl() {
      const url = new URL(window.location.href);

      if (this.currentPage > 1) {
        url.searchParams.set(PAGE_PARAM, String(this.currentPage));
      } else {
        url.searchParams.delete(PAGE_PARAM);
      }

      if (url.toString() !== window.location.href) {
        history.replaceState(history.state, '', url.toString());
      }
    }
  }

  customElements.define('collection-card-pager', CollectionCardPager);
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined' && !customElements.get('collection-card-pager')) {
  defineCollectionCardPager();
}
